import { useTheme } from '../../hooks/useTheme'
import type { ReactNode } from 'react'

interface ButtonGroupOption {
  value: string
  label: ReactNode
  icon?: string
}

interface ButtonGroupProps {
  options: ButtonGroupOption[]
  value: string
  onChange: (value: string) => void
  variant?: 'primary' | 'secondary'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  className?: string
}

export function ButtonGroup({
  options,
  value,
  onChange,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
}: ButtonGroupProps) {
  const theme = useTheme()
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-6 py-4 text-lg',
  }
  
  const getButtonStyle = (isActive: boolean) => {
    if (variant === 'primary') {
      return {
        backgroundColor: isActive 
          ? 'rgba(0, 136, 204, 0.2)' 
          : theme.cardBg,
        border: isActive
          ? '2px solid rgba(0, 136, 204, 0.5)'
          : `1px solid ${theme.cardBorder}`,
        color: theme.textColor,
      }
    } else {
      return {
        backgroundColor: isActive ? theme.cardHoverBg : theme.cardBg,
        border: `1px solid ${theme.cardBorder}`,
        color: theme.textColor,
      }
    }
  }
  
  return (
    <div className={`flex gap-2 ${fullWidth ? 'w-full' : ''} ${className}`}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`flex-1 ${sizeClasses[size]} rounded-xl font-semibold transition-all hover:opacity-90`}
          style={getButtonStyle(value === option.value)}
        >
          {option.icon && <span className="mr-2">{option.icon}</span>}
          {option.label}
        </button>
      ))}
    </div>
  )
}
